import { stringify } from 'qs';
import { ConflictMode, HasBooked, Repeat, Room, Area, Order, RoomPage, RoomSchedule, RoomPageV2, AreaV2, RoomV2, RoomServiceConfig } from 'type/room';
import Request from './request';
import { DefaultSuccess } from 'type/http';

const request = new Request('room');

export interface GetBookRoomParam {
  /**
   * 名称，当名称不为空时，后面的条件均失效，仅仅根据名称来查询
   */
  name?: string;
  /**
   * 区域id
   */
  areaId: string;
  /**
   * 可容纳人数
   */
  capacity?: number;
  /**
   * 设备类型，多个类型使用“,”分隔
   */
  deviceType?: number;
  /**
   * 会议开始时间，格式：yyyy-MM-dd HH:mm
   */
  startTime: string;
  /**
   * 会议结束时间，格式：yyyy-MM-dd HH:mm
   */
  endTime: string;
  /**
   * 搜索关键词
   */
  keyword?: string;
  /**
   * 重复预约：0-不重复，1-每日，2-每周，3-每月;
   */
  repeat?: Repeat;
  /**
   * 重复日期：; 当repeat=2时，使用数字1～7代表星期一至星期天，多个时间使用“,”分隔；; 当repeat=3时，使用数字1～31表示重复日期
   */
  repeatDate?: number;
  /**
   * 重复结束日期，当repeat不为0时必填
   */
  repeatEndDate?: number;
  /**
   * 是否显示已预约信息：0-不显示，1-显示，默认为0;
   */
  hasBooked?: HasBooked;
  /**
   * 冲突模式：0-时间有交集即冲突，1-全包含才冲突，默认为0，为0时还需要过滤不可预约的时间段的会议室;
   */
  conflictMode?: ConflictMode;
  /**
   * 用途：1-会议室，2-休闲室，3-接待室，4-培训室，5-报告厅，6-办公室，7-其它，多个使用逗号分割
   */
  usage?: string;
  /**
   * 是否可用
   */
  isEnable?: boolean;
  /**
   * 一页的记录数
   */
  pageSize?: number;
  /**
   * 当前页码
   */
  pageNo?: number;
  /**
   * 是否需要推荐
   */
  needRecommend?: boolean;
}

export interface GetBookRoomParamV2 {
  pageSize: number;
  pageNo: number;
  attributeSelectOptions?: {
    hasRoomFullName: boolean;
    hashDevices: boolean;
    hasRoomBookRule: boolean;
    hasAuditConfig: boolean;
    hasStatus: boolean;
  };
  roomStatusSelectOptions?: null | {
    startTime?: string | number;
    endTime?: string | number;
    statusList?: string | string[] | null;
  };
  roomPermissionSelectOptions?: {
    isFilterNoPermissionRoom: boolean;
  };
  areaIdList?: string[] | null;
  name?: string | null;
}

export interface GetRoomSchedulesV2 {
  roomIdList?: string[];
  startTime?: number | string;
  endTime?: number | string;
}

export interface GetRoomWithSchedulesV2 {
  fetchRoomParams: GetBookRoomParamV2;
  fetchScheduleParams: Omit<GetRoomSchedulesV2, 'roomIdList'>;
}

export const getBookRoom = (params: GetBookRoomParam) => {
  return request.get<RoomPage>(`/api/user/rooms/actions/book-by-page?${stringify(params)}&needAuditConfig=true`);
};

export const getAreas = () => {
  return request.get<Area[]>('/api/user/areas/trees');
};
export interface GetOrderParam {
  /**
   * 房间列表，多个id用,分隔
   */
  roomIds: string;
  /**
   * 房间名称
   */
  roomName?: string;
  /**
   * 会议开始时间，格式：yyyy-MM-dd HH:mm
   */
  startTime: string;
  /**
   * 会议结束时间，格式：yyyy-MM-dd HH:mm
   */
  endTime: string;
  /**
   * 会议室所在区域的id字符串，支持多个企业，使用,进行拼接
   */
  areaId: string;
}

export const getOrder = (params: GetOrderParam) => {
  return request.get<Order[]>(`/api/user/orders?${stringify(params)}`);
};

/**
 * 根据会议室获取会议室详情
 */
export const getRoomDetail = (id: string) => {
  return request.get<Room>(`/api/user/rooms/${id}`, { params: { needRoomAuditConfig: true } });
};

/**
 * 根据会议室获取会议室详情-V2
 */
export const getRoomDetailV2 = (id: string) => {
  return request.post<DefaultSuccess<RoomV2>>(`/api/user/v2/rooms/${id}`, {
    hasRoomFullName: true,
    hashDevices: true,
    hasRoomBookRule: true,
    hasAuditConfig: true,
    hasStatus: true
  });
};

/**
 * 获取会议室列表-V2
 */
export const getBookRoomV2 = (params: GetBookRoomParamV2) => {
  return request.post<DefaultSuccess<RoomPageV2>>('/api/user/v2/rooms/actions/page', params);
};

/**
 * 获取会议室日程-V2
 */
export const getRoomSchedulesV2 = (params: GetRoomSchedulesV2) => {
  return request.post<DefaultSuccess<RoomSchedule[]>>('/api/user/v2/orders/actions/list', params);
};

/**
 * 获取区域数据-V2
 */
export const getAreasV2 = () => {
  return request.get<DefaultSuccess<AreaV2[]>>('/api/user/v2/areas/trees');
};

export const getRoomServiceConfig = (params: string[]) => {
  return request.post<DefaultSuccess<RoomServiceConfig>>('/api/user/v2/meetings/room-service-config', params);
};
